
import { motion } from "framer-motion";
import { useEffect } from "react";
import heroVideo from "../assests/animation.mp4"; // 🎥 Your hero background video

export default function AnimationProject() {
   
  return (
    <div className="bg-gray-50 text-gray-900 font-orbitron">
      {/* Hero Section with Video Background */}
      <section className="relative w-full h-[70vh] text-white flex items-center justify-center text-center">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>

        <div className="relative z-10 px-6 max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6 tracking-tight drop-shadow-lg"
          >
            2D & 3D Animation Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed text-gray-200"
          >
            Bringing your ideas to life with immersive animations that tell stories, explain concepts, and engage audiences.
          </motion.p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 text-gray-900">
          Why Animation Matters
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed">
          Animation transcends words, making complex ideas understandable and engaging. Whether 2D or 3D, we transform concepts into visually striking experiences tailored to your audience.
        </p>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900">
          Our Animation Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            { title: "🖌 2D Animation", desc: "Explainer videos, motion graphics, and character animation." },
            { title: "🎥 3D Animation", desc: "Product demos, architectural visualization, and cinematic animations." },
            { title: "🎞 Storyboarding & Concept Design", desc: "Planning and visualizing your animation from start to finish." },
            { title: "🌍 AR/VR Animation", desc: "Immersive experiences for education, gaming, and marketing." },
            { title: "🛠 Post-Production & Effects", desc: "Sound integration, special effects, and final polishing." },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition"
            >
              <h3 className="text-xl font-semibold mb-3 text-indigo-700">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900">
          How We Work
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-6xl mx-auto text-center">
          {[
            "Understand goals & audience",
            "Concept design & storyboarding",
            "Animate in 2D or 3D",
            "Add effects, textures & sound",
            "Review, refine & deliver",
          ].map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
              viewport={{ once: true }}
              className="p-6 bg-white rounded-xl shadow-md hover:shadow-lg"
            >
              <div className="text-4xl mb-3">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-700 leading-relaxed">{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900">
          Applications
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {[
            { title: "🎬 Entertainment & Films", desc: "Movies, series, and shorts." },
            { title: "📱 Advertising & Marketing", desc: "Animated campaigns and product demos." },
            { title: "🏫 Education & E-Learning", desc: "Engaging explainer videos and tutorials." },
            { title: "🎮 Gaming & VR/AR", desc: "Game cinematics and immersive experiences." },
            { title: "🏢 Corporate", desc: "Presentations, training, and brand storytelling." },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition"
            >
              <h3 className="font-semibold mb-2 text-lg text-indigo-700">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <h2 className="text-4xl md:text-5xl font-bold mb-6">
          Bring Your Ideas to Life with Stunning Animations
        </h2>
        <p className="text-lg max-w-xl mx-auto mb-6">
          Engage your audience and tell stories that leave a lasting impression with our expert 2D & 3D animation services.
        </p>
        <motion.button
          whileHover={{ scale: 1.1 }}
          className="bg-white text-indigo-700 font-semibold px-6 py-3 rounded-xl shadow-md hover:bg-gray-100 transition"
        >
          🎨 Get Started Today
        </motion.button>
      </section>
    </div>
  );
}
